import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { Anchor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import FieldCopyText from '@atlassian/jira-common-components-field-copy-text/src/view.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CUSTOMER_ACCESS } from '@atlassian/jira-servicedesk-customer-permissions/src/common/types.tsx';
import { CustomerAccessStatus } from '@atlassian/jira-servicedesk-customer-permissions/src/common/ui/customer-access-status/index.tsx';
import messages from './messages';

type Props = {
	isNextGen: boolean;
	isOpenAccess: boolean;
	canAdministerJira: boolean;
	portalUrlLink: string;
	customerPermissionsLink: string;
	portalShortLink: string;
	projectId: number;
	intl: IntlShape;
};

export const PortalDetails = (props: Props) => {
	const {
		portalUrlLink,
		customerPermissionsLink,
		portalShortLink,
		projectId,
		isNextGen,
		isOpenAccess,
		canAdministerJira,
		intl: { formatMessage },
	} = props;

	const customizationDesc = canAdministerJira
		? messages.customizationDesc
		: messages.customizationDescNotAdmin;

	return (
		<>
			<Container>
				<MiniContainer>
					<FieldCopyText
						label={formatMessage(messages.portalUrl)}
						tooltipLabel={formatMessage(messages.copyUrl)}
						value={portalUrlLink}
						shouldFitContainer
						onCopyClick={(event, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'copyPortalURL', {
								projectId,
							});
						}}
					/>
				</MiniContainer>
				{isNextGen && (
					<>
						<HeadingContainer>
							<Heading level="h200">{formatMessage(messages.channelAccess)}</Heading>
						</HeadingContainer>

						<CustomerAccessStatus
							accessType={isOpenAccess ? CUSTOMER_ACCESS.PUBLIC : CUSTOMER_ACCESS.RESTRICTED}
							settingsLink={customerPermissionsLink}
						/>
						<HeadingContainer>
							<Heading level="h200">{formatMessage(messages.customization)}</Heading>
						</HeadingContainer>
						<p>{formatMessage(customizationDesc)}</p>
						<p>
							<Anchor href={portalShortLink} target="_blank">
								{formatMessage(messages.customizationLink)}
							</Anchor>
						</p>
					</>
				)}
			</Container>
		</>
	);
};

export default injectIntl(PortalDetails);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 100}px`,
	paddingBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MiniContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 45}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	marginTop: token('space.200', '16px'),
});
